import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  BhgCallDetailPreview,
  BhgDashboardScheduledPreview,
  BhgMobileClearPreview,
  BrowserFrame,
  PhoneFrame,
  UaDashboardReportPreview,
  UaMobileClearPreview,
} from "@constructorfleet/ui/design-system";

const ROTATION_MS = 6000;

interface SceneBase {
  id: string;
  notification: {
    title: string;
    body: ReactNode;
    tone: "urgent" | "ok";
  };
}

interface BrowserScene extends SceneBase {
  variant: "browser";
  url: string;
  render: () => ReactNode;
}

interface PhoneScene extends SceneBase {
  variant: "phone";
  render: () => ReactNode;
}

type Scene = BrowserScene | PhoneScene;

const SCENES: Scene[] = [
  {
    id: "checkin-dash-scheduled",
    variant: "browser",
    url: "app.constructorfleet.com/dashboard",
    notification: {
      title: "BHG Check-in Assist",
      body: (
        <>
          Today you are scheduled for <b>Drug Screen</b> by 3:00 PM.
        </>
      ),
      tone: "urgent",
    },
    render: () => <BhgDashboardScheduledPreview />,
  },
  {
    id: "checkin-call-detail",
    variant: "browser",
    url: "app.constructorfleet.com/dashboard?callSid=CA-2026-001",
    notification: {
      title: "BHG Check-in Assist",
      body: (
        <>
          Call completed. Result: <b>Drug Screen</b>. Transcript saved.
        </>
      ),
      tone: "urgent",
    },
    render: () => <BhgCallDetailPreview />,
  },
  {
    id: "checkin-bottle-check",
    variant: "browser",
    url: "app.constructorfleet.com/dashboard",
    notification: {
      title: "BHG Check-in Assist",
      body: (
        <>
          Tomorrow you have a <b>Bottle Check</b> — bring your bottles in.
        </>
      ),
      tone: "urgent",
    },
    render: () => <BhgDashboardScheduledPreview />,
  },
  {
    id: "checkin-bottle-and-screen",
    variant: "browser",
    url: "app.constructorfleet.com/dashboard",
    notification: {
      title: "BHG Check-in Assist",
      body: (
        <>
          Today: <b>Bottle Check + Drug Screen</b>. Go in before dosing closes.
        </>
      ),
      tone: "urgent",
    },
    render: () => <BhgDashboardScheduledPreview />,
  },
  {
    id: "checkin-mobile-clear",
    variant: "phone",
    notification: {
      title: "BHG Check-in Assist",
      body: (
        <>
          Today's check-in complete. <b>No visit required.</b>
        </>
      ),
      tone: "ok",
    },
    render: () => <BhgMobileClearPreview />,
  },
  {
    id: "ua-dash-report",
    variant: "browser",
    url: "app.constructorfleet.com/dashboard",
    notification: {
      title: "UA Notifications",
      body: (
        <>
          Your color came up — <b>report by 4:00 PM</b>.
        </>
      ),
      tone: "urgent",
    },
    render: () => <UaDashboardReportPreview />,
  },
  {
    id: "ua-mobile-clear",
    variant: "phone",
    notification: {
      title: "UA Notifications",
      body: (
        <>
          Color did not come up. <b>No report needed.</b>
        </>
      ),
      tone: "ok",
    },
    render: () => <UaMobileClearPreview />,
  },
];

export function HomeHeroViz() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const prefersReducedMotion = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    prefersReducedMotion.current = mq.matches;
  }, []);

  useEffect(() => {
    if (paused || prefersReducedMotion.current) return;
    const timer = window.setTimeout(() => {
      setIndex((current) => (current + 1) % SCENES.length);
    }, ROTATION_MS);
    return () => window.clearTimeout(timer);
  }, [index, paused]);

  const scene = SCENES[index];

  const toneClass = useMemo(() => {
    if (scene.notification.tone === "urgent") return "hv-toast hv-toast-urgent";
    if (scene.notification.tone === "ok") return "hv-toast hv-toast-ok";
    return "hv-toast";
  }, [scene.notification.tone]);

  return (
    <div
      className="hv-root"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="Product preview"
    >
      <div className="hv-stage">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={scene.id}
            className="hv-scene"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -16, scale: 0.98 }}
            transition={{ duration: 0.55, ease: [0.16, 0.84, 0.44, 1] }}
          >
            {/* The frame mockups are decorative product screenshots rendered in
                the DOM. Hide them from the accessibility tree and remove their
                fake buttons/links from the tab order so they don't compete with
                the real navigation (e.g. duplicate "Account" controls). */}
            {scene.variant === "browser" ? (
              <div className="hv-browser-wrap" aria-hidden="true" inert>
                <BrowserFrame url={scene.url}>
                  <div className="hv-scale-browser">{scene.render()}</div>
                </BrowserFrame>
              </div>
            ) : (
              <div className="hv-phone-wrap" aria-hidden="true" inert>
                <PhoneFrame width={300} height={500} time="5:04">
                  <div className="hv-scale-phone">{scene.render()}</div>
                </PhoneFrame>
              </div>
            )}
            <div className={toneClass} role="status" aria-live="polite">
              <div className="hv-toast-brand">
                <span className="hv-toast-badge">CF</span>
                <span className="hv-toast-title">{scene.notification.title}</span>
                <span className="hv-toast-time">now</span>
              </div>
              <div className="hv-toast-body">{scene.notification.body}</div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="hv-dots" role="tablist" aria-label="Preview scene">
        {SCENES.map((s, i) => (
          <button
            key={s.id}
            type="button"
            role="tab"
            aria-selected={i === index}
            aria-label={`Show scene ${i + 1} of ${SCENES.length}`}
            className={["hv-dot", i === index && "is-active"].filter(Boolean).join(" ")}
            onClick={() => setIndex(i)}
          />
        ))}
      </div>
    </div>
  );
}
